<template>
    <section class="layout">
        <ds-modal
            class="regular-modal"
            show-close
            :is-open="isOpen"
            :sub-title="fullName"
            :size="modalSize"
            :title.sync="modalTitle"
            @close="modalClose"
        >
            <template #header>
                <ds-button
                    v-if="showAction"
                    data-qa="save-button"
                    :loading="saving"
                    :disabled="actionDisabled"
                    @click="save"
                >
                    Save
                </ds-button>
            </template>

            <template #default>
                <div class="layout">
                    <div>
                        <ds-input-field
                            v-model="appointments.title"
                            class="input-border"
                            name="Title"
                            label="Title"
                            required
                            :submitted="submitted"
                        />

                        <ds-checkbox
                            v-model="appointments.allDay"
                            class="option-checkbox"
                            name="allDay"
                            label="All day"
                            @input="allDayEnabled"
                        />

                        <div class="date-fields">
                            <ds-date-picker
                                v-model="appointments.startDate"
                                class="date-field"
                                name="startDate"
                                label="Start date"
                            />

                            <span class="date-space"> at </span>

                            <ds-select-field
                                v-model="startTime"
                                :options="getTimeDropDowns"
                                :readonly="isAllDayEnabled"
                                bind-value-only
                                class="date-field"
                                name="startTime"
                                label="Start Time"
                                required
                                :submitted="submitted"
                            />
                        </div>

                        <div v-if="!isAllDayEnabled" class="date-fields">
                            <ds-date-picker
                                v-model="appointments.endDate"
                                class="date-field"
                                name="endDate"
                                label="End date"
                            />

                            <span class="date-space"> at </span>

                            <ds-select-field
                                v-model="endTime"
                                :options="getTimeDropDowns"
                                bind-value-only
                                class="date-field"
                                name="endTime"
                                label="End Time"
                                required
                                :submitted="submitted"
                            />
                        </div>

                        <ds-input-field
                            v-model="appointments.location"
                            class="input-border"
                            name="location"
                            label="Location"
                        />
                    </div>

                    <div class="moreOptions">
                        <ds-text-button v-if="!moreOptions" data-qa="show-more-options" @click="showMoreOptions">
                            <span>More Options <ds-icon class="chevron" name="chevron-down" /></span>
                        </ds-text-button>

                        <ds-text-button v-else data-qa="hide-more-options" @click="hideMoreOptions">
                            <span>More Options <ds-icon class="chevron" name="chevron-up" /></span>
                        </ds-text-button>
                    </div>

                    <div v-if="moreOptions">
                        <ds-text-area-field
                            v-model="appointments.notes"
                            type="text"
                            name="notes"
                            label="Notes"
                            data-qa="appointments-notes"
                        />

                        <ds-select-field
                            v-model="appointments.assignedUserId"
                            :options="ownersDrop"
                            bind-value-only
                            class="input-border"
                            name="owners"
                            label="Assigned to"
                        />

                        <ds-select-field
                            v-model="appointments.priority"
                            :options="priorities"
                            bind-value-only
                            class="input-border"
                            name="priority"
                            label="Priority"
                        />

                        <ds-multiselect
                            v-model="appointments.userIds"
                            bind-value-only
                            class="input-border"
                            searchable
                            multiple
                            :placeholder="multiSelectPlaceholder"
                            :options="ownersDrop"
                        />

                        <ds-checkbox
                            v-model="appointments.notifyOwner"
                            class="option-checkbox"
                            name="sendNotification"
                            label="Send individual notifications"
                        />
                    </div>
                </div>
            </template>
        </ds-modal>
    </section>
</template>
<script>
import moment from 'moment-timezone';
import axios from 'axios';

export default {
    props: {
        contact: Object,
        userId: [String, Number],
    },

    data() {
        return {
            isOpen: false,
            showAction: false,
            actionDisabled: false,
            modalSize: 'med',
            modalTitle: 'Add an appointment',
            options: {},
            isAllDayEnabled: false,
            timeZoneIdServer: '',
            timeZoneIdUser: '',
            appointments: {
                id: '',
                startDate: '',
                endDate: '',
                priority: '',
                notes: '',
                title: null,
                location: '',
                assignedUserId: 0,
                contactId: 0,
                userIds: [],
                notifyOwner: false,
                allDay: false,
            },
            saving: false,
            startTime: '',
            endTime: '',
            priorities: [],
            moreOptions: false,
            multiSelectPlaceholder: 'Notify users',
            submitted: false,
        };
    },

    created() {
        this.appointments.contactId = Number(this.contact.id);
        this.appointments.assignedUserId = Number(this.userId);
    },

    mounted() {
        this.$root.$on('DROPDOWN_LOADED', (dropDownData) => {
            this.options = dropDownData;
        });
        this.initTab();
    },

    beforeDestroy() {
        this.$root.$off('DROPDOWN_LOADED');
    },

    computed: {

        disableSave() {
            return !this.appointments.title || !this.startTime;
        },

        currentDate() {
            const date = moment.tz(this.timeZoneIdUser).format('YYYY-MM-DD');

            return moment(date).millisecond(0).add(0, 'days').toDate();
        },

        fullName() {
            return `${this.contact.firstName} ${this.contact.lastName}`;
        },

        ownersDrop() {
            return this.options.owners;
        },

        currentTime() {
            const timeReminder = Math.ceil(moment.tz(this.timeZoneIdUser).minute() / 15);
            let roundedTime = (timeReminder - 1) * 15;

            if (timeReminder === 0) {
                roundedTime = timeReminder * 15;
            }

            return moment.tz(this.timeZoneIdUser).minute(roundedTime).format('hh:mm A');
        },

        currentEndTime() {
            const timeReminder = Math.ceil(moment.tz(this.timeZoneIdUser).minute() / 15);
            let roundedTime = (timeReminder + 1) * 15;

            if (timeReminder === 0) {
                roundedTime = timeReminder * 15;
            }

            return moment.tz(this.timeZoneIdUser).minute(roundedTime).format('hh:mm A');
        },

        getTimeDropDowns() {
            const times = [];

            for (let i = 0; i < 12; i++) {
                let label = `${i < 10 ? `0${i}` : i}:`;
                let value = `${i < 10 ? `0${i}` : i}:`;

                if (i === 0) {
                    label = '12:';
                    value = '12:';
                }

                for (let j = 0; j < 60; j += 15) {
                    const slabel = label + (j < 10 ? `0${j}` : j);
                    const svalue = value + (j < 10 ? `0${j}` : j);

                    times.push({ label: slabel, value: svalue });
                }
            }
            let time = times.map((hourValue) => ({ ...hourValue }));
            const timesAm = time.map((hourValue) => {
                hourValue.label += ' AM';
                hourValue.value += ' AM';

                return hourValue;
            });

            time = times.map((hourValue) => ({ ...hourValue }));

            const timesPm = time.map((hourValue) => {
                hourValue.label += ' PM';
                hourValue.value += ' PM';

                return hourValue;
            });

            return timesAm.concat(timesPm);
        },
    },

    methods: {
        showModal(id) {
            this.initTab();
            this.showAction = true;
            this.actionDisabled = false;
            this.appointments.assignedUserId = Number(this.userId);
            this.appointments.startDate = this.currentDate;
            this.appointments.endDate = this.currentDate;
            this.appointments.contactId = Number(this.contact.id);
            this.startTime = this.currentTime.toString();
            this.endTime = this.currentEndTime.toString();


            if (id > 0) {
                this.modalTitle = 'Edit appointment';
                this.load(id);
                this.appointments.startDate = this.appointments.startDate.toString();
            }

            this.isOpen = true;
        },

        save() {
            this.appointments.startDate = this.updateStartDateWithTime();
            this.appointments.endDate = this.updateEndDateWithTime();
            const isFormValid = this.isFormValid();

            if (isFormValid && (this.appointments.id === null || this.appointments.id === '')) {
                this.saving = true;
                this.submitted = true;
                this.addAppointment(this.appointments);
            } else if (isFormValid && (this.appointments.id != null && this.appointments.id > 0)) {
                this.saving = true;

                if (this.appointments.userIds === null) {
                    this.appointments.userIds = [];
                }
                this.updateAppointment(this.appointments);
            } else {
                this.submitted = true;
                this.saving = false;
            }
        },

        modalClose() {
            this.nestedModal_reset();
            this.isOpen = false;
        },

        showMoreOptions() {
            this.moreOptions = true;
        },

        hideMoreOptions() {
            this.moreOptions = false;
        },

        updateStartDateWithTime() {
            if (this.appointments.startDate === null) {
                return this.appointments.startDate;
            }

            let momentStartDate = moment(this.appointments.startDate).hours(this.getHourFromMoment(this.startTime));

            momentStartDate = momentStartDate.minutes(moment(this.startTime, 'hh:mm A').get('minute'));

            return this.getServerTZDate(momentStartDate);
        },

        updateEndDateWithTime() {
            if (this.appointments.endDate === null) {
                return this.appointments.endDate;
            }

            let momentEndDate = moment(this.appointments.endDate).hours(this.getHourFromMoment(this.endTime));

            momentEndDate = momentEndDate.minutes(moment(this.endTime, 'hh:mm A').get('minute'));

            return this.getServerTZDate(momentEndDate);
        },

        getServerTZDate(momentStartDate) {
            const strtimewithformat = moment(momentStartDate).format('YYYY-MM-DD HH:mm');

            const date = moment.tz(strtimewithformat.concat(''), this.timeZoneIdServer);

            const formatterdDate = date.format();

            return moment.tz(formatterdDate.concat(''), this.timeZoneIdServer).toDate();
        },

        getHourFromMoment(momentStartTime) {
            if (momentStartTime === '12:00 AM' || momentStartTime === '12:15 AM' || momentStartTime === '12:30 AM' || momentStartTime === '12:45 AM') {
                const data = momentStartTime.replace('12:', '00:');

                return moment(data, 'hh:mm A').get('hour');
            }

            return moment(momentStartTime, 'hh:mm A').get('hour');
        },

        allDayEnabled() {
            this.isAllDayEnabled = this.appointments.allDay;
        },

        async load(id) {
            try {
                const response = await axios.get(`/app/appointments/loadAppointmentById/${id}`);

                this.appointments = response.data;
                this.startTime = moment(this.appointments.startDate).tz(this.timeZoneIdUser).format('hh:mm A').toString();
                this.endTime = moment(this.appointments.endDate).tz(this.timeZoneIdUser).format('hh:mm A').toString();

                const startDate = moment.tz(this.appointments.startDate, this.timeZoneIdUser).format('YYYY-MM-DD');
                const endDate = moment.tz(this.appointments.endDate, this.timeZoneIdUser).format('YYYY-MM-DD');

                this.appointments.startDate = moment(startDate).toDate();
                this.appointments.endDate = moment(endDate).toDate();
                this.allDayEnabled();
            } catch (error) {
                this.$error({ message: `Unable to get Appointment Details by id:${id}` });
            }
        },

        async addAppointment() {
            const successMessage = 'Appointment added successfully';
            const errorMessage = 'Unable to Save Appointment';

            try {
                await axios.post('/app/appointments/addAppointment', this.appointments);
                this.$toast({ message: successMessage });
                this.$root.$emit('APPOINTMENTS_STATUS', true);
                this.modalClose();
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        async updateAppointment() {
            const successMessage = 'Appointment updated successfully';
            const errorMessage = 'Unable to update Appointment';

            try {
                await axios.put('/app/appointments/updateAppointment', this.appointments);
                this.$toast({ message: successMessage });
                this.$root.$emit('APPOINTMENTS_STATUS', true);
                this.modalClose();
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        async loadPriorityDropDown() {
            try {
                const response = await axios.get('/app/appointments/loadDropDown');

                this.priorities = response.data.priorities;
                this.priorities = Object.entries(this.priorities).map((key) => {
                    return { label: key[1], value: Number(key[0]) };
                });
            } catch (error) {
                this.$error({ message: 'Unable to load priorities ' });
            }
        },

        initTab() {
            this.loadPriorityDropDown();
            this.getAppTimeZones();
        },

        async getAppTimeZones() {
            try {
                const response = await axios.get('/app/appointments/getTimeZones');

                this.timeZoneIdUser = response.data.userTimeZone;
                this.timeZoneIdServer = response.data.serverTimeZone;
            } catch (error) {
                this.$error({ message: 'unable to get user Time Zone' });
            }
        },

        nestedModal_reset() {
            this.showAction = false;
            this.modalTitle = 'Add an appointment';
            this.saving = false;
            this.startTime = '';
            this.endTime = '';
            this.moreOptions = false;
            this.multiSelectPlaceholder = 'Notify users';
            this.submitted = false;
            this.appointments.title = null;
            this.appointments.assignedUserId = Number(this.userId);
            this.appointments.allDay = false;
            this.appointments.startDate = null;
            this.appointments.endDate = null;
            this.appointments.priority = '';
            this.appointments.notes = '';
            this.appointments.location = '';
            this.appointments.notifyOwner = false;
            this.appointments.userIds = [];
            this.appointments.id = null;
            this.isAllDayEnabled = false;
        },

        isFormValid() {
            let successMessage = '';

            if (this.appointments.title === null || this.appointments.title === '') {
                successMessage = 'Title is required';
                this.$toast({ message: successMessage });

                return false;
            }

            if (this.appointments.startDate === null || this.appointments.startDate.toString() === '') {
                successMessage = 'Start Date is required';
                this.$toast({ message: successMessage });

                return false;
            }

            if (this.appointments.allDay) {
                return true;
            }

            if (this.appointments.endDate === null || this.appointments.endDate.toString() === '') {
                successMessage = 'End Date is required';
                this.$toast({ message: successMessage });

                return false;
            }

            if (this.appointments.startDate >= this.appointments.endDate) {
                successMessage = 'The end date is before the start date. Please update and save again.';
                this.$toast({ message: successMessage });

                return false;
            }

            return true;
        },
    },
};

</script>

<style lang="scss" scoped>
.date-fields {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0;
}

.layout {
    display: flex;
    flex-direction: column;
}

.date-space {
    margin: $spacing-100 $spacing-200 0 $spacing-200;
    vertical-align: center;
}

.button-space {
    margin-left: $spacing-200;
}

.date-field {
    flex: 2;
}

.chevron {
    bottom: -$spacing-050;
    position: relative;
    --icon-size: 1.25rem;
}

.input-border {
    width: 100%;
}

.moreOptions {
    display: flex;
    margin: auto;
    margin-bottom: $spacing-400;
}

.long {
    margin-top: 80rem;
}

@media screen and ($small) {
    .input-row {
        display: flex;
        flex-direction: column;
    }

    .layout {
        flex-direction: column;
        margin: auto;
    }
}
</style>
